import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ChartBar, 
  MagnifyingGlass,
  CurrencyDollar,
  Plus,
  ArrowUp,
  ArrowDown
} from '@phosphor-icons/react';
import SortableTable from '../components/SortableTable';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReconciliationPage() {
  const [transactions, setTransactions] = useState([]);
  const [statements, setStatements] = useState([]);
  const [claims, setClaims] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    transaction_type: '',
    status: '',
    search: ''
  });
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [claimForm, setClaimForm] = useState({
    order_id: '',
    claim_type: 'general',
    reason: '',
    amount: ''
  });

  useEffect(() => {
    fetchData();
  }, [filters.transaction_type, filters.status]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.transaction_type) params.append('transaction_type', filters.transaction_type);
      if (filters.status) params.append('status', filters.status);
      
      const [transactionsRes, statementsRes, claimsRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/reconciliation/transactions?${params}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/reconciliation/statements`, { withCredentials: true }),
        axios.get(`${API_URL}/api/reconciliation/claims`, { withCredentials: true }),
        axios.get(`${API_URL}/api/reconciliation/summary`, { withCredentials: true })
      ]);
      
      setTransactions(transactionsRes.data);
      setStatements(statementsRes.data);
      setClaims(claimsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch reconciliation data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleRaiseClaim = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/reconciliation/claims`, {
        ...claimForm,
        amount: parseFloat(claimForm.amount)
      }, { withCredentials: true });
      
      toast.success('Claim raised successfully');
      setClaimDialogOpen(false);
      setClaimForm({ order_id: '', claim_type: 'general', reason: '', amount: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to raise claim');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getTransactionColor = (type) => {
    if (type === 'order_payment') return 'success';
    if (type === 'commission' || type === 'return_refund' || type === 'fee') return 'danger';
    return 'neutral';
  };

  const filteredTransactions = transactions.filter(t => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return t.order_id?.toLowerCase().includes(search) || t.transaction_id?.toLowerCase().includes(search);
    }
    return true;
  });

  const handleExportTransactions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reconciliation/export`, { withCredentials: true });
      const txns = response.data.transactions;
      if (txns.length === 0) { toast.error('No transactions to export'); return; }
      const headers = ['transaction_id', 'order_id', 'transaction_type', 'amount', 'status', 'settlement_cycle', 'created_at'];
      const csv = [
        headers.join(','),
        ...txns.map(t => headers.map(h => `"${(t[h] ?? '').toString().replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `transactions_${new Date().toISOString().slice(0,10)}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Transactions exported');
    } catch { toast.error('Export failed'); }
  };

  return (
    <DashboardLayout 
      title="Reconciliation" 
      subtitle="Track payments, settlements, and claims"
      actions={
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={handleExportTransactions}
            className="border-[#e5e5ea]"
            data-testid="export-transactions-btn"
          >
            Export CSV
          </Button>
          <Button 
            onClick={() => setClaimDialogOpen(true)}
            className="bg-[#7d64ff] hover:bg-[#6153a7] text-white"
            data-testid="raise-claim-btn"
          >
            <Plus size={18} className="mr-2" />
            Raise Claim
          </Button>
        </div>
      }
    >
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-[#e5e5ea] p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#28cd8c]/10 flex items-center justify-center rounded">
                <ArrowUp size={20} className="text-[#28cd8c]" weight="bold" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Total Revenue</p>
                <p className="text-xl font-bold text-[#28cd8c]">{formatCurrency(summary.total_revenue)}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-[#e5e5ea] p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#ea4b5a]/10 flex items-center justify-center rounded">
                <ArrowDown size={20} className="text-[#ea4b5a]" weight="bold" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Commissions</p>
                <p className="text-xl font-bold text-[#ea4b5a]">{formatCurrency(summary.total_commissions)}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-[#e5e5ea] p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#ea4b5a]/10 flex items-center justify-center rounded">
                <ArrowDown size={20} className="text-[#ea4b5a]" weight="bold" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Refunds</p>
                <p className="text-xl font-bold text-[#ea4b5a]">{formatCurrency(summary.total_refunds)}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-[#e5e5ea] p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#f6a609]/10 flex items-center justify-center rounded">
                <CurrencyDollar size={20} className="text-[#d18f08]" weight="bold" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Pending</p>
                <p className="text-xl font-bold text-[#d18f08]">{formatCurrency(summary.pending_settlement)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <Tabs defaultValue="transactions" className="space-y-4">
        <TabsList className="bg-white border border-[#e5e5ea] p-1">
          <TabsTrigger value="transactions" data-testid="transactions-tab">Transactions</TabsTrigger>
          <TabsTrigger value="statements" data-testid="statements-tab">Statements</TabsTrigger>
          <TabsTrigger value="claims" data-testid="claims-tab">Claims</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions">
          {/* Filters */}
          <div className="bg-white border border-[#e5e5ea] mb-4">
            <div className="action-bar">
              <div className="relative flex-1 max-w-md">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5a5c87]" size={18} />
                <Input
                  placeholder="Search by Order ID..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10 rounded-sm"
                  data-testid="transaction-search"
                />
              </div>
              
              <Select value={filters.transaction_type || "all"} onValueChange={(value) => setFilters({ ...filters, transaction_type: value === "all" ? "" : value })}>
                <SelectTrigger className="w-44 rounded-sm" data-testid="type-filter">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="order_payment">Order Payment</SelectItem>
                  <SelectItem value="commission">Commission</SelectItem>
                  <SelectItem value="return_refund">Return Refund</SelectItem>
                  <SelectItem value="fee">Fee</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.status || "all"} onValueChange={(value) => setFilters({ ...filters, status: value === "all" ? "" : value })}>
                <SelectTrigger className="w-36 rounded-sm" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="settled">Settled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Transactions Table */}
          {loading ? (
            <div className="loading-spinner"><div className="spinner"></div></div>
          ) : (
            <SortableTable
              columns={[
                { key: 'transaction_id', label: 'Transaction ID', render: v => <span className="font-mono text-sm">{v?.slice(-10)}</span> },
                { key: 'order_id', label: 'Order ID', render: v => <span className="font-mono text-sm">{v?.slice(-10) || '-'}</span> },
                { key: 'transaction_type', label: 'Type', render: v => <span className="text-xs font-semibold uppercase tracking-wider">{v?.replace('_', ' ')}</span> },
                { key: 'amount', label: 'Amount', render: v => <span className={`font-bold ${v >= 0 ? 'text-[#28cd8c]' : 'text-[#ea4b5a]'}`}>{v >= 0 ? '+' : ''}{formatCurrency(v)}</span> },
                { key: 'settlement_cycle', label: 'Cycle', render: v => v || '-' },
                { key: 'status', label: 'Status', render: v => <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded ${v === 'settled' ? 'sp-badge-success' : 'sp-badge-warning'}`}>{v}</span> },
                { key: 'created_at', label: 'Date', render: v => formatDate(v) },
              ]}
              data={filteredTransactions}
              pageSize={15}
              emptyMessage="No transactions found"
              emptyIcon={ChartBar}
            />
          )}
        </TabsContent>

        <TabsContent value="statements">
          <SortableTable
            columns={[
              { key: 'statement_id', label: 'Statement ID', render: (v, row) => <span className="font-mono text-sm">{v || `STM-${row._idx + 1}`}</span> },
              { key: 'period', label: 'Period', render: v => v || '-' },
              { key: 'total_amount', label: 'Total', render: v => <span className="font-bold">{formatCurrency(v)}</span> },
              { key: 'status', label: 'Status', render: v => <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded sp-badge-success">{v || 'Generated'}</span> },
              { key: 'created_at', label: 'Date', render: v => formatDate(v) },
            ]}
            data={statements.map((s, i) => ({ ...s, _idx: i }))}
            pageSize={10}
            emptyMessage="No statements yet"
            emptyIcon={ChartBar}
          />
        </TabsContent>

        <TabsContent value="claims">
          <SortableTable
            columns={[
              { key: 'claim_id', label: 'Claim ID', render: v => <span className="font-mono text-sm">{v?.slice(-10)}</span> },
              { key: 'claim_type', label: 'Type', render: v => <span className="text-xs font-semibold uppercase tracking-wider">{v?.replace('_', ' ')}</span> },
              { key: 'reason', label: 'Reason', render: v => <span className="truncate max-w-[200px] inline-block">{v}</span> },
              { key: 'amount', label: 'Amount', render: v => v ? <span className="font-bold">{formatCurrency(v)}</span> : '-' },
              { key: 'status', label: 'Status', render: v => <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded ${v === 'resolved' ? 'sp-badge-success' : v === 'rejected' ? 'sp-badge-danger' : 'sp-badge-warning'}`}>{v}</span> },
              { key: 'created_at', label: 'Date', render: v => formatDate(v) },
            ]}
            data={claims}
            pageSize={10}
            emptyMessage="No claims"
            emptyIcon={ChartBar}
          />
        </TabsContent>
      </Tabs>

      {/* Raise Claim Dialog */}
      <Dialog open={claimDialogOpen} onOpenChange={setClaimDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-bold">Raise New Claim</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRaiseClaim} className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Order ID</label>
              <Input
                value={claimForm.order_id}
                onChange={(e) => setClaimForm({ ...claimForm, order_id: e.target.value })}
                placeholder="Enter order ID"
                className="mt-1 rounded-sm"
                data-testid="claim-order-id"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Claim Type</label>
              <Select value={claimForm.claim_type} onValueChange={(value) => setClaimForm({ ...claimForm, claim_type: value })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="claim-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="lost_in_transit">Lost in Transit</SelectItem>
                  <SelectItem value="damaged">Damaged</SelectItem>
                  <SelectItem value="overcharge">Overcharge</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Reason *</label>
              <Input
                value={claimForm.reason}
                onChange={(e) => setClaimForm({ ...claimForm, reason: e.target.value })}
                required
                placeholder="Describe the issue"
                className="mt-1 rounded-sm"
                data-testid="claim-reason"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Amount (₹)</label>
              <Input
                type="number"
                value={claimForm.amount}
                onChange={(e) => setClaimForm({ ...claimForm, amount: e.target.value })}
                placeholder="Claim amount"
                className="mt-1 rounded-sm"
                data-testid="claim-amount"
              />
            </div>
            <div className="flex gap-4 pt-4">
              <Button type="button" variant="outline" onClick={() => setClaimDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="submit-claim-btn">
                Submit Claim
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
